// public/js/auth/TokenManager.js - ADMIN VERSION

import { createLogger } from '@utils/logger.js';
import config from '@/config.js';

/**
 * TokenManager handles token storage, retrieval, and refreshing
 * @class TokenManager
 */
export default class TokenManager {
    constructor() {
      this.logger = createLogger('TokenManager');

      // Use admin-specific keys from config
      this.accessToken = localStorage.getItem(config.tokenKey) || null;
      this.tokenType = localStorage.getItem('tokenType') || 'Bearer';
      this.expiresAt = localStorage.getItem('admin_expiresAt') ? parseInt(localStorage.getItem('admin_expiresAt')) : null;
      this.userInfo = localStorage.getItem(config.userKey) ? JSON.parse(localStorage.getItem(config.userKey)) : null;

      // Auto-refresh control
      this.autoRefreshPaused = false;

      // Set up refresh timer if token exists and not expired
      if (this.accessToken && this.expiresAt && this.expiresAt > Date.now()) {
        this._setRefreshTimer();
      }
    }
  
    /**
     * Store token data in localStorage and memory
     * @param {Object} data - Auth data from server
     */
    storeTokens(data) {
      this.accessToken = data.accessToken;
      this.tokenType = data.tokenType;
      this.userInfo = data.user;

      // Calculate token expiry
      const expiresIn = this._parseExpiresIn(data.expiresIn);
      this.expiresAt = Date.now() + expiresIn;

      // Save to local storage using admin-specific keys
      localStorage.setItem(config.tokenKey, this.accessToken);
      localStorage.setItem('tokenType', this.tokenType);
      localStorage.setItem('admin_expiresAt', this.expiresAt);
      localStorage.setItem(config.userKey, JSON.stringify(this.userInfo));

      // Set refresh timer
      this._setRefreshTimer();
    }
  
    /**
     * Clear all token data
     */
    clearTokens() {
      // Clear refresh timer
      if (this._refreshTimer) {
        clearTimeout(this._refreshTimer);
      }

      // Clear local storage using admin-specific keys
      localStorage.removeItem(config.tokenKey);
      localStorage.removeItem('tokenType');
      localStorage.removeItem('admin_expiresAt');
      localStorage.removeItem(config.userKey);
      
      // Clear memory
      this.accessToken = null;
      this.tokenType = null;
      this.expiresAt = null;
      this.userInfo = null;
    }
  
    /**
     * Get current access token
     */
    getAccessToken() {
      return this.accessToken;
    }
  
    /**
     * Get current user info
     */
    getUserInfo() {
      return this.userInfo;
    }
  
    /**
     * Get token type (usually 'Bearer')
     */
    getTokenType() {
      return this.tokenType;
    }
  
    /**
     * Get valid access token, refreshing if necessary
     */
    async getValidAccessToken() {
      // If token is expired or about to expire, refresh it
      if (!this.accessToken || !this.expiresAt || Date.now() >= this.expiresAt - 60000) {
        await this.refreshToken();
      }
      
      return this.accessToken;
    }
  
    /**
     * Validate if current token is still valid
     */
    async validateToken() {
      // Check if token exists and is not expired
      if (this.accessToken && this.expiresAt && Date.now() < this.expiresAt) {
        return true;
      }
      
      // Try to refresh the token if it exists but is expired
      if (this.accessToken && this.expiresAt) {
        try {
          await this.refreshToken();
          return true;
        } catch (error) {
          return false;
        }
      }
      
      return false;
    }
  
    /**
     * Refresh the access token
     */
    async refreshToken() {
      try {
        const response = await fetch('/api/auth/refresh-token', {
          method: 'POST',
          credentials: 'include'  // Include cookies for refresh token
        });
        
        if (!response.ok) {
          // If refresh fails, clear local data
          this.clearTokens();
          throw new Error('Session expired. Please log in again.');
        }
        
        const data = await response.json();
        
        // Update token information
        this.accessToken = data.accessToken;
        this.tokenType = data.tokenType;

        // Calculate token expiry
        const expiresIn = this._parseExpiresIn(data.expiresIn);
        this.expiresAt = Date.now() + expiresIn;

        // Save to local storage using admin-specific keys (must match storeTokens)
        localStorage.setItem(config.tokenKey, this.accessToken);
        localStorage.setItem('tokenType', this.tokenType);
        localStorage.setItem('admin_expiresAt', this.expiresAt);
        
        // Set new refresh timer
        this._setRefreshTimer();
        
        return true;
      } catch (error) {
        this.logger.error('Token refresh failed:', error);
        throw error;
      }
    }
  
    /**
     * Parse expires in string (e.g., '1h') to milliseconds
     * @private
     */
    _parseExpiresIn(expiresIn) {
      if (typeof expiresIn === 'number') {
        return expiresIn * 1000; // Convert seconds to milliseconds
      }
      
      const match = expiresIn.match(/^(\d+)([smhd])$/);
      if (!match) {
        // Default to 1 hour if format is unrecognized
        return 3600 * 1000;
      }
      
      const value = parseInt(match[1], 10);
      const unit = match[2];
      
      switch (unit) {
        case 's': return value * 1000; // seconds
        case 'm': return value * 60 * 1000; // minutes
        case 'h': return value * 60 * 60 * 1000; // hours
        case 'd': return value * 24 * 60 * 60 * 1000; // days
        default: return 3600 * 1000; // default to 1 hour
      }
    }
  
    /**
     * Set timer to refresh token before it expires
     * @private
     */
    _setRefreshTimer() {
      if (this._refreshTimer) {
        clearTimeout(this._refreshTimer);
      }

      if (!this.expiresAt) return;

      // Don't set timer if auto-refresh is paused
      if (this.autoRefreshPaused) {
        this.logger.debug('Auto-refresh is paused, timer not set');
        return;
      }

      const timeToExpiry = this.expiresAt - Date.now();
      // Refresh at 80% of expiry time to provide a safe margin
      const refreshTime = Math.max(timeToExpiry * 0.8, 1000); // Ensure at least 1 second

      this._refreshTimer = setTimeout(() => {
        this.refreshToken().catch(err => {
          this.logger.error('Auto refresh token failed:', err);
        });
      }, refreshTime);
    }

    /**
     * Pause automatic token refresh (used during inactivity)
     */
    pauseAutoRefresh() {
      if (this.autoRefreshPaused) return;

      this.autoRefreshPaused = true;

      // Clear existing timer
      if (this._refreshTimer) {
        clearTimeout(this._refreshTimer);
        this._refreshTimer = null;
      }

      this.logger.info('Auto-refresh paused');
    }

    /**
     * Resume automatic token refresh (used when activity detected)
     */
    resumeAutoRefresh() {
      if (!this.autoRefreshPaused) return;

      this.autoRefreshPaused = false;

      // Restart the refresh timer
      if (this.accessToken && this.expiresAt) {
        this._setRefreshTimer();
      }

      this.logger.info('Auto-refresh resumed');
    }
  }